import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
    format,
    addMonths,
    subMonths,
    setMonth,
    setYear,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    parse,
    isValid
} from 'date-fns';
import {
    ChevronLeft,
    ChevronRight,
    CalendarDays
} from 'lucide-react';
import { fetchHFAvailableDates } from '../../lib/hf-utils';

export interface HistoricalDatePickerProps {
    symbol: string;
    currentDateStr: string | null; // e.g. "2026-04-12"
    onChange: (dateStr: string) => void;
    className?: string;
    hideIcon?: boolean;
    disabled?: boolean;
    placeholder?: string;
}

const MONTH_SHORT = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const WEEKDAY_NAMES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

export const HistoricalDatePicker: React.FC<HistoricalDatePickerProps> = ({
    symbol,
    currentDateStr,
    onChange,
    className,
    hideIcon = false,
    disabled = false,
    placeholder = 'Select Date'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewMode, setViewMode] = useState<'calendar' | 'month-year'>('calendar');
    const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
    
    const triggerRef = useRef<HTMLButtonElement | HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    // Safely parse current selected date
    const parsedCurrentDate = useMemo(() => {
        if (!currentDateStr) return new Date();
        const parsed = parse(currentDateStr, 'yyyy-MM-dd', new Date());
        return isValid(parsed) ? parsed : new Date();
    }, [currentDateStr]);

    // Calendar view tracking date
    const [viewDate, setViewDate] = useState<Date>(parsedCurrentDate);

    // Available dates loaded from Cloud Archive for current view month
    const [availableDays, setAvailableDays] = useState<Set<number>>(new Set());

    // Synchronize viewDate when currentDateStr changes externally
    useEffect(() => {
        if (currentDateStr) {
            const parsed = parse(currentDateStr, 'yyyy-MM-dd', new Date());
            if (isValid(parsed)) {
                setViewDate(parsed);
            }
        }
    }, [currentDateStr]);

    // Position calculation with intelligent boundary collision detection
    const updatePosition = useCallback(() => {
        if (!triggerRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();
        const popoverWidth = 320;
        const popoverHeight = 410;
        const margin = 12;

        let left = rect.left;
        let top = rect.bottom + 8;

        // Prevent right edge overflow
        if (left + popoverWidth > window.innerWidth - margin) {
            left = Math.max(margin, window.innerWidth - popoverWidth - margin);
        }

        // Prevent left edge underflow
        if (left < margin) {
            left = margin;
        }

        // Prevent bottom edge overflow (flip to open upward if space below is constrained)
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow < popoverHeight && spaceAbove > popoverHeight) {
            top = Math.max(margin, rect.top - popoverHeight - 8);
        } else if (spaceBelow < popoverHeight) {
            top = Math.max(margin, window.innerHeight - popoverHeight - margin);
        }

        setPopoverStyle({
            position: 'fixed',
            top: `${top}px`,
            left: `${left}px`,
            width: `${popoverWidth}px`,
            zIndex: 99999
        });
    }, []);

    // Handle outside clicks and keyboard navigation
    useEffect(() => {
        if (!isOpen) return;

        updatePosition();

        const handleClickOutside = (event: MouseEvent) => {
            if (
                popoverRef.current && !popoverRef.current.contains(event.target as Node) &&
                triggerRef.current && !triggerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                setViewMode('calendar');
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                setViewMode('calendar');
            }
        };

        const handleResizeOrScroll = () => {
            updatePosition();
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('resize', handleResizeOrScroll);
        window.addEventListener('scroll', handleResizeOrScroll, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', handleResizeOrScroll);
            window.removeEventListener('scroll', handleResizeOrScroll, true);
        };
    }, [isOpen, updatePosition]);

    // Fetch cloud availability for the active viewing month
    useEffect(() => {
        if (!isOpen) return;

        let isMounted = true;
        const loadAvailability = async () => {
            const year = viewDate.getFullYear();
            const month = viewDate.getMonth() + 1; // 1-indexed for backend

            try {
                const daysSet = await fetchHFAvailableDates(symbol, year, month);
                if (isMounted) {
                    setAvailableDays(daysSet);
                }
            } catch (err) {
                if (isMounted) setAvailableDays(new Set());
            }
        };

        loadAvailability();
        return () => { isMounted = false; };
    }, [viewDate, symbol, isOpen]);

    // Month & Year Navigation
    const nextMonth = () => setViewDate(prev => addMonths(prev, 1));
    const prevMonth = () => setViewDate(prev => subMonths(prev, 1));

    const handleSelectDate = (date: Date) => {
        const formattedDate = format(date, 'yyyy-MM-dd');
        onChange(formattedDate);
        setIsOpen(false);
        setViewMode('calendar');
    };

    // Calendar Interval Computations
    const monthStart = startOfMonth(viewDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday start
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const daysInterval = eachDayOfInterval({ start: startDate, end: endDate });

    const formattedDisplayDate = useMemo(() => {
        if (!currentDateStr) return placeholder;
        return format(parsedCurrentDate, 'dd-MM-yyyy');
    }, [currentDateStr, parsedCurrentDate, placeholder]);

    const currentYear = viewDate.getFullYear();
    const yearsList = useMemo(() => {
        const thisYear = new Date().getFullYear();
        const list = [];
        for (let y = thisYear - 3; y <= thisYear + 1; y++) {
            list.push(y);
        }
        return list;
    }, []);

    const isCustomTrigger = Boolean(className);

    return (
        <div className="relative inline-flex items-center">
            {/* Trigger Button */}
            <button
                type="button"
                ref={triggerRef as any}
                onClick={() => {
                    if (disabled) return;
                    if (!isOpen) {
                        updatePosition();
                        setIsOpen(true);
                    } else {
                        setIsOpen(false);
                        setViewMode('calendar');
                    }
                }}
                disabled={disabled}
                className={className || "group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-slate-100/90 dark:bg-slate-800/90 hover:bg-amber-50 dark:hover:bg-amber-950/40 border border-slate-200/80 dark:border-slate-700/80 hover:border-amber-400/60 dark:hover:border-amber-500/60 text-slate-700 dark:text-slate-200 transition-all duration-200 shadow-sm cursor-pointer select-none"}
            >
                {!hideIcon && (
                    <CalendarDays className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'scale-110 text-amber-500' : 'text-slate-400 dark:text-slate-400 group-hover:text-amber-500'}`} />
                )}
                
                <span className={`text-[12px] md:text-[13px] font-black tracking-tight font-mono transition-colors uppercase whitespace-nowrap ${isCustomTrigger ? 'flex-1 text-left' : 'group-hover:text-amber-600 dark:group-hover:text-amber-400'}`}>
                    {formattedDisplayDate}
                </span>
            </button>

            {/* Portal Dropdown Popover */}
            {isOpen && typeof document !== 'undefined' && createPortal(
                <div
                    ref={popoverRef}
                    style={popoverStyle}
                    className="fixed z-[99999] p-5 bg-white dark:bg-[#0e1626] rounded-3xl border border-slate-100 dark:border-slate-800 shadow-[0_20px_50px_rgba(0,0,0,0.12)] dark:shadow-[0_25px_60px_rgba(0,0,0,0.7)] select-none animate-in fade-in zoom-in-95 duration-150"
                >
                    {/* Header: Prev Button, Month Year Title, Circular Next Button */}
                    <div className="flex items-center justify-between mb-4">
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); prevMonth(); }}
                            className="p-2 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                        >
                            <ChevronLeft className="w-5 h-5 stroke-[2.5]" />
                        </button>

                        <button
                            type="button"
                            onClick={() => setViewMode(prev => prev === 'calendar' ? 'month-year' : 'calendar')}
                            className="text-base font-black text-[#1e293b] dark:text-slate-100 hover:text-teal-700 dark:hover:text-teal-300 tracking-tight transition-colors cursor-pointer"
                        >
                            {format(viewDate, 'MMMM yyyy')}
                        </button>

                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); nextMonth(); }}
                            className="w-8 h-8 rounded-full border-2 border-[#1e293b] dark:border-slate-200 flex items-center justify-center text-[#1e293b] dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-transform active:scale-95 cursor-pointer"
                        >
                            <ChevronRight className="w-4 h-4 stroke-[3]" />
                        </button>
                    </div>

                    {/* View 1: Calendar Grid View */}
                    {viewMode === 'calendar' ? (
                        <>
                            {/* Weekday Header */}
                            <div className="grid grid-cols-7 gap-1.5 mb-2">
                                {WEEKDAY_NAMES.map((name) => (
                                    <div
                                        key={name}
                                        className="text-center text-[12px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider py-0.5"
                                    >
                                        {name}
                                    </div>
                                ))}
                            </div>

                            {/* Calendar Days Matrix (Square Pill Design) */}
                            <div className="grid grid-cols-7 gap-1.5">
                                {daysInterval.map((day, idx) => {
                                    const isCurrentMonth = isSameMonth(day, viewDate);
                                    const dayNumber = day.getDate();
                                    const isSelected = isSameDay(day, parsedCurrentDate);
                                    const isAvailable = isCurrentMonth && availableDays.has(dayNumber);

                                    let cellStyles = "relative w-9.5 h-9.5 mx-auto flex flex-col items-center justify-center rounded-xl transition-all duration-150 ";

                                    if (!isCurrentMonth) {
                                        cellStyles += "text-slate-300 dark:text-slate-700 cursor-not-allowed opacity-35";
                                    } else if (isSelected) {
                                        cellStyles += "bg-[#0c4a45] dark:bg-teal-600 border border-[#0c4a45] text-white font-black shadow-md shadow-[#0c4a45]/30 ring-2 ring-[#0c4a45]/20 ring-offset-2 dark:ring-offset-[#0e1626] scale-105 z-10 cursor-pointer";
                                    } else if (isAvailable) {
                                        cellStyles += "bg-[#e6f4f1] dark:bg-teal-950/70 border border-[#b2ded7] dark:border-teal-700/70 text-[#0c4a45] dark:text-teal-200 font-extrabold shadow-2xs hover:scale-105 active:scale-95 cursor-pointer";
                                    } else {
                                        cellStyles += "text-slate-600 dark:text-slate-400 font-bold hover:bg-slate-100 dark:hover:bg-slate-800/60 cursor-pointer";
                                    }

                                    return (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => isCurrentMonth && handleSelectDate(day)}
                                            disabled={!isCurrentMonth}
                                            className={cellStyles}
                                        >
                                            <span className={`text-[13px] font-mono leading-none ${isSelected ? 'font-black text-white' : isAvailable ? 'font-black text-[#0c4a45] dark:text-teal-200' : 'font-bold'}`}>
                                                {format(day, 'd')}
                                            </span>

                                            {/* Indicator Dot */}
                                            {isCurrentMonth && (
                                                <span className={`w-1 h-1 rounded-full mt-1 ${isSelected ? 'bg-white/80' : isAvailable ? 'bg-[#0c4a45] dark:bg-teal-400' : 'bg-slate-300 dark:bg-slate-600'}`} />
                                            )}

                                            {!isCurrentMonth && (
                                                <span className="w-1 h-1 rounded-full mt-1 bg-slate-200 dark:bg-slate-800" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Bottom Legend */}
                            <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-center gap-6">
                                <div className="flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-[#0c4a45] dark:bg-teal-400" />
                                    <span className="text-[11px] font-black tracking-wider text-slate-600 dark:text-slate-300 uppercase">AVAILABLE</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600" />
                                    <span className="text-[11px] font-black tracking-wider text-slate-600 dark:text-slate-300 uppercase">MISSING</span>
                                </div>
                            </div>
                        </>
                    ) : (
                        /* View 2: Fast Month & Year Selector Grid */
                        <div className="flex flex-col gap-3 py-1 animate-in fade-in duration-150">
                            <div className="flex flex-col gap-1">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                    Select Year
                                </span>
                                <div className="grid grid-cols-5 gap-1">
                                    {yearsList.map(year => (
                                        <button
                                            key={year}
                                            type="button"
                                            onClick={() => setViewDate(prev => setYear(prev, year))}
                                            className={`py-1.5 rounded-xl text-xs font-mono font-black transition-all cursor-pointer ${year === currentYear ? 'bg-[#0c4a45] text-white font-black shadow-md' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-teal-50 dark:hover:bg-teal-950/40'}`}
                                        >
                                            {year}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex flex-col gap-1">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                    Select Month
                                </span>
                                <div className="grid grid-cols-3 gap-1.5">
                                    {MONTH_SHORT.map((mName, mIdx) => {
                                        const isSelectedMonth = viewDate.getMonth() === mIdx;
                                        return (
                                            <button
                                                key={mName}
                                                type="button"
                                                onClick={() => {
                                                    setViewDate(prev => setMonth(prev, mIdx));
                                                    setViewMode('calendar');
                                                }}
                                                className={`py-2 px-1 rounded-xl text-xs font-mono font-bold transition-all cursor-pointer ${isSelectedMonth ? 'bg-[#0c4a45] text-white font-black shadow-md' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                                            >
                                                {mName}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};
